/**
 * StatusPill — shared status-badge component.
 *
 * Visual contract matches `AmbassadorRow.jsx` (Документы page):
 *   <span className="badge" style={{ background, color }}>LABEL</span>
 *
 * Solid palette + white text, no transparent rgba variants. Phase 61 W7 originally
 * wrapped `.badge--success` etc, but those CSS variants render with light rgba
 * backgrounds + 1px borders that read poorly at 11px uppercase with letter-spacing.
 * Switched to solid colors matching `lib/contractStatus.js` palette so all status
 * pills across the app share one visual language.
 *
 * Consumers (the five importers, P-D2 D11):
 *   - components/InvoiceStatusDropdown.jsx (ContractCard passes appearance='chip'; InvoicesView does not)
 *   - components/InvoicesView.jsx
 *   - components/BulkInvoiceStatusModal.jsx
 *   - components/outreach/campaigns/CampaignSettingsPanel.jsx
 *   - components/outreach/campaigns/CampaignAccessPanel.tsx
 *
 * `withDot` / `pulse` props are kept for backward compat with W7 call sites but
 * are intentionally ignored — the solid color carries the signal without an
 * extra circle. If a strong "live / pulsing" indicator is needed later, build it
 * as a separate `LiveDot` component, not by re-introducing visual noise here.
 */
import React from 'react';

export type Variant = 'success' | 'warning' | 'yellow' | 'orange' | 'danger' | 'darkred' | 'info' | 'accent' | 'neutral' | 'muted';

export type Appearance = 'solid' | 'chip';

export interface StatusPillProps {
  label: string;
  variant: Variant;
  /** default `'solid'` — `'chip'` is the lead card's tinted 22 px pill (P-D2 D14) */
  appearance?: Appearance;
  /** kept for backward compat; ignored — see file header */
  withDot?: boolean;
  /** kept for backward compat; ignored — see file header */
  pulse?: boolean;
}

const CHIP_TONE: Record<Variant, string> = {
  success: 'green', warning: 'orange', yellow: 'limeyellow', orange: 'orange',
  danger: 'red', darkred: 'red', info: 'blue', accent: 'purple',
  neutral: 'neutral', muted: 'neutral',
};

const PALETTE: Record<Variant, { background: string; color: string }> = {
  success: { background: '#10b981', color: '#ffffff' },
  warning: { background: '#f59e0b', color: '#ffffff' },
  yellow:  { background: '#eab308', color: '#ffffff' },
  orange:  { background: '#f97316', color: '#ffffff' },
  danger:  { background: '#ef4444', color: '#ffffff' },
  darkred: { background: '#b91c1c', color: '#ffffff' },
  info:    { background: '#3b82f6', color: '#ffffff' },
  accent:  { background: '#a855f7', color: '#ffffff' },
  neutral: { background: '#94a3b8', color: '#ffffff' },
  muted:   { background: 'var(--neutral-30)', color: 'var(--neutral-80)' },
};

export const StatusPill: React.FC<StatusPillProps> = ({ label, variant, appearance = 'solid' }) => {
  // No `badge` class and no inline style: `.invoice-status-dropdown .badge`
  // (styles.css:992, min-width:140px) would otherwise outrank the card's scoped rule.
  if (appearance === 'chip') {
    return <span className={`oc-lead-card__chip oc-lead-card__chip--${CHIP_TONE[variant]}`}>{label}</span>;
  }
  const { background, color } = PALETTE[variant];
  return (
    <span className="badge" style={{ background, color, border: 'none' }}>
      {label}
    </span>
  );
};

export default StatusPill;
